import { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { useApp } from "../lib/store";
import { EntityForm } from "./EntityForm";
import { OrgChart } from "./OrgChart";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Users, Plus, Mail, Phone } from "lucide-react";

/**
 * Full supplier contact management (mirrors customer contacts).
 * - Fixed supplier (supplierId set): supplier-detail "Kontakte" tab.
 * - Global (no supplierId): standalone "Lieferanten-Kontakte" page (scope=supplier).
 * Create/edit/delete go through the SRM cap-gated endpoints (supplier_write).
 */
export function SupplierContactsPanel({ supplierId, supplierName, canWrite }) {
  const { t, plLabel } = useApp();
  const [contacts, setContacts] = useState(null);
  const [editing, setEditing] = useState(null); // null | {} (new) | contact (edit)

  const load = useCallback(() => {
    const url = supplierId
      ? `/contacts?account_ids=${supplierId}&limit=500`
      : `/contacts?scope=supplier&limit=500`;
    api.get(url).then((r) => setContacts(r.data || [])).catch(() => setContacts([]));
  }, [supplierId]);

  useEffect(load, [load]);

  const openEdit = (c) => {
    if (!canWrite) return;
    api.get(`/contacts/${c.id}`).then((r) => setEditing(r.data)).catch(() => setEditing(c));
  };

  const createUrl = supplierId ? `/suppliers/${supplierId}/contacts` : `/suppliers/contacts`;
  const newInitial = supplierId ? { account_ids: [supplierId], account_names: [supplierName] } : {};

  if (!contacts) return <div className="text-gray-400">{t("loading") || "Lädt…"}</div>;

  return (
    <div className="space-y-6" data-testid="supplier-contacts-panel">
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4 text-[#1B2A4A]" />
            <h3 className="font-semibold text-[#1B2A4A]">{t("contacts")}</h3>
          </div>
          {canWrite && (
            <Button size="sm" data-testid="supplier-contact-add-btn"
              className="bg-[#1B2A4A] hover:bg-[#24365d]" onClick={() => setEditing(newInitial)}>
              <Plus className="h-4 w-4 mr-1" /> {t("new_contact")}
            </Button>
          )}
        </div>
        {contacts.length === 0 ? (
          <p className="p-6 text-sm text-gray-400">{t("empty_title")}</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("name")}</TableHead>
                <TableHead>{t("buying_role")}</TableHead>
                {!supplierId && <TableHead>{t("suppliers")}</TableHead>}
                <TableHead>{t("email")}</TableHead>
                <TableHead>{t("phone")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {contacts.map((c) => (
                <TableRow key={c.id} data-testid={`supplier-contact-${c.id}`}
                  className={canWrite ? "cursor-pointer hover:bg-amber-50/40" : ""}
                  onClick={() => openEdit(c)}>
                  <TableCell className="font-medium text-[#1B2A4A]">
                    {[c.first_name, c.last_name].filter(Boolean).join(" ") || "–"}
                    {c.title ? <span className="text-gray-400 font-normal"> · {c.title}</span> : null}
                  </TableCell>
                  <TableCell>{c.buying_role ? <Badge variant="outline">{plLabel("buying_roles", c.buying_role)}</Badge> : "–"}</TableCell>
                  {!supplierId && <TableCell className="text-sm text-gray-600">{(c.account_names || []).join(", ") || "–"}</TableCell>}
                  <TableCell className="text-sm">{c.email ? <a href={`mailto:${c.email}`} onClick={(e) => e.stopPropagation()} className="text-[#1B2A4A] hover:underline inline-flex items-center gap-1"><Mail className="h-3 w-3" />{c.email}</a> : "–"}</TableCell>
                  <TableCell className="text-sm">{c.phone || c.mobile ? <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3 text-gray-400" />{c.phone || c.mobile}</span> : "–"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <OrgChart contacts={contacts} />

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="supplier-contact-dialog">
          <DialogHeader>
            <DialogTitle>{editing?.id ? t("edit") : t("new_contact")}</DialogTitle>
          </DialogHeader>
          {editing && (
            <EntityForm module="contacts" initial={editing}
              createUrl={createUrl}
              itemUrl={(id) => `/suppliers/contacts/${id}`}
              confirmSave allowDelete
              onSaved={() => { setEditing(null); load(); }}
              onDeleted={() => { setEditing(null); load(); }}
              onCancel={() => setEditing(null)} />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
